import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle, NdBadge } from "@/components/Kpi";
import { COLORS, ComparisonChart, PieBreakdown } from "@/components/Charts";
import { usePalette } from "@/lib/palette";
import { BarreAvanzamento } from "@/components/BarreAvanzamento";
import { ClassificaRete } from "@/components/ClassificaRete";
import { FilterBar, useFiltersMeta, ANNO_CORRENTE } from "@/components/Filters";
import { eurShort, num } from "@/lib/format";
import GrigliaDashboard from "@/components/GrigliaDashboard";

/** Esclude il residuo "Altro"; ordine alfabetico con Gestione HUB in coda. */
const ordina = (rows) => rows
  .filter((r) => r.slug !== "altro")
  .sort((a, b) => (a.slug === "gestione-hub" ? 1 : b.slug === "gestione-hub" ? -1
    : a.nome.localeCompare(b.nome, "it")));

const COPPIE = [
  { key: "totale", macro: null, etichetta: "TOTALE" },
  { key: "auto", macro: "AUTO", etichetta: "AUTO" },
  { key: "pos", macro: "NON AUTO", etichetta: "NON AUTO (POS)" },
];

const SENZA_PREMI = ["linda-alfieri", "gestione-hub", "altro"];

const torta = (p) => [p.caricato, p.liquidato, "#0284C7", "#F59E0B", "#7C3AED", "#BE185D"];

const shortName = (nome = "") => {
  const p = nome.trim().split(/\s+/);
  return p.length < 2 ? nome : `${p[0]} ${p[p.length - 1][0]}.`;
};

/** Performance commerciali: classifica + grafici di confronto per commerciale. */
export default function PerformanceCommerciali() {
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: ANNO_CORRENTE });
  const [rows, setRows] = useState([]);
  const [budgetAgenzia, setBudgetAgenzia] = useState(null);
  const [perMacro, setPerMacro] = useState({});
  const navigate = useNavigate();
  const anno = Number(filters.anno);
  const palette = usePalette();
  const TORTA = torta(palette);

  useEffect(() => {
    api.get(`/dashboard/overview?${qs({ anno: filters.anno })}`)
      .then((r) => {
        setRows(ordina(r.data.ranking_commerciali || []));
        setBudgetAgenzia(r.data.budget_liquidato || null);
      })
      .catch(() => { setRows([]); setBudgetAgenzia(null); });
    Promise.all(["AUTO", "NON AUTO"].map((macro) =>
      api.get(`/dashboard/overview?${qs({ anno: filters.anno, macro })}`)
        .then((r) => [macro, { rows: ordina(r.data.ranking_commerciali || []),
          budget: r.data.budget_liquidato || null }])
        .catch(() => [macro, { rows: [], budget: null }])))
      .then((esiti) => setPerMacro(Object.fromEntries(esiti)));
  }, [filters]);

  const chartData = useMemo(() => rows.map((r) => ({
    label: r.slug === "gestione-hub" ? "Gestione HUB" : shortName(r.nome),
    caricato: r.importo_caricato,
    budget_caricato: r.budget_importo,
    liquidato: r.importo_liquidato,
    budget_liquidato: r.budget_liquidato,
    raggiungimento: r.raggiungimento_perc,
    pratiche: r.pratiche_caricate,
    pratiche_liquidate: r.pratiche_liquidate,
    premi: r.premi_importo,
    budget_premi: r.budget_premi,
    slug: r.slug,
  })), [rows]);

  const barre = (campo, campoBudget) => chartData.map((r) => ({
    label: r.label, slug: r.slug, valore: r[campo], budget: r[campoBudget],
  }));

  return (
    <div data-testid="performance-page">
      <SectionTitle sub={`Classifica e confronto della rete commerciale ${anno}. Clicca un commerciale per aprire la sua dashboard.`}
        right={
          <a href={`/api/report/performance-commerciali.pdf?${qs({ anno: filters.anno })}`}
            data-testid="btn-report-performance-commerciali-pdf" target="_blank" rel="noreferrer"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
            Scarica report PDF
          </a>
        }>
        Performance commerciali
      </SectionTitle>
      <FilterBar meta={meta} value={filters} onChange={setFilters} fields={["anno"]} />

      {rows.length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="performance-empty">
          <NdBadge label="nessun dato disponibile per il periodo selezionato" />
        </Card>
      ) : (
        <>
          <GrigliaDashboard pagina="performance" filtri={filters} className="mt-8" blocchi={[
            { id: "chart-perf-importi", label: "Caricato e liquidato per commerciale", span: 2,
              node: <ComparisonChart testId="chart-perf-importi" height={320}
                title={`Caricato e liquidato per commerciale · ${anno}`} data={chartData}
                keys={[{ key: "caricato", name: "Caricato", color: COLORS.caricato },
                  { key: "liquidato", name: "Liquidato", color: COLORS.liquidato }]} /> },
            { id: "chart-perf-pratiche", label: "Pratiche per commerciale", span: 2,
              node: <ComparisonChart testId="chart-perf-pratiche" height={320} labelFormat="int"
                title={`Pratiche caricate e liquidate per commerciale · ${anno}`} data={chartData}
                keys={[{ key: "pratiche", name: "Caricate", color: COLORS.caricato },
                  { key: "pratiche_liquidate", name: "Liquidate", color: COLORS.liquidato }]} /> },
            { id: "chart-perf-progress", label: "Avanzamento sui budget", span: 4,
              node: (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="perf-progress-row">
                  <BarreAvanzamento testId="chart-perf-caricato" colore={COLORS.caricato}
                    titolo="Caricato vs budget" sottotitolo="progressivo sul budget annuo"
                    righe={barre("caricato", "budget_caricato")} />
                  <BarreAvanzamento testId="chart-perf-liquidato" colore={COLORS.liquidato}
                    titolo="Liquidato vs budget" sottotitolo="progressivo sul budget annuo"
                    righe={barre("liquidato", "budget_liquidato")} />
                  <BarreAvanzamento testId="chart-perf-premi" colore="#0284C7"
                    titolo="Premi assicurativi vs budget" sottotitolo="CPI + CVT + NFK liquidati"
                    righe={barre("premi", "budget_premi").filter((r) => !SENZA_PREMI.includes(r.slug))} />
                </div>
              ) },
            ...COPPIE.flatMap(({ key, macro, etichetta }) => {
              const dati = macro
                ? (perMacro[macro] || { rows: [], budget: null })
                : { rows, budget: budgetAgenzia };
              const torta = dati.rows.filter((r) => r.budget_liquidato).map((r, i) => ({
                name: r.slug === "gestione-hub" ? "Gestione HUB" : shortName(r.nome),
                value: r.budget_liquidato, color: TORTA[i % TORTA.length] }));
              return [
                { id: `chart-perf-incidenza-${key}`, span: 1, sezione: etichetta,
                  label: `Incidenza sul budget liquidato · ${etichetta}`,
                  node: <PieBreakdown testId={`chart-perf-incidenza-${key}`} height={280}
                    title={`Incidenza sul budget liquidato · ${etichetta} · ${anno}`} data={torta} /> },
                { id: `ranking-${key}`, span: 3, sezione: etichetta,
                  label: `Classifica completa · ${etichetta}`,
                  node: <ClassificaRete testId={`ranking-${key}`} rows={dati.rows}
                    scostamentoRete={dati.budget?.scostamento_perc ?? null}
                    scostamentoReteImporto={dati.budget?.scostamento_importo ?? null}
                    titolo={`Classifica completa · ${etichetta} · clicca le colonne per ordinare`} /> },
              ];
            }),
          ]} />

        </>
      )}
    </div>
  );
}
